import Image from "next/image";
import { TierFrameAvatar } from "@/components/TierFrameAvatar";
import type { LeaderboardEntry } from "@/lib/domain/leaderboard";
import { cn } from "@/lib/utils";
import { getTierAccent } from "@/utils/tierVisuals";
import { useLocale } from "@/contexts/LocaleContext";
import { useActiveEventMode } from "@/lib/hooks/useActiveEventMode";
import { useTierLabel } from "@/hooks/useTierLabel";

interface LeaderboardTableProps {
  entries: LeaderboardEntry[];
  currentUserId?: string;
  onEntryClick?: (userId: string) => void;
  /**
   * Force the event (Betsson/World Cup) styling regardless of region. The
   * landing page passes this so the leaderboard matches the rest of its event
   * UI (gated on `eventEnabled`); in-app surfaces leave it undefined and fall
   * back to the region-gated `isEventMode`.
   */
  eventMode?: boolean;
  /**
   * Column heading for the points column. Defaults to the ranked "RP" label;
   * the auction board passes its "AP" label instead.
   */
  pointsLabel?: string;
  /**
   * Compact rows for the public game pages, whose board sits in a narrow
   * column: small avatar, wider name column, tier on up to two lines, nothing
   * clipped. The app leaderboard keeps its full-size layout.
   */
  compact?: boolean;
}

const poppins = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  letterSpacing: "0",
  lineHeight: 1,
} as const;

export function LeaderboardTable({ entries, currentUserId, onEntryClick, eventMode, pointsLabel, compact = false }: LeaderboardTableProps) {
  const { t } = useLocale();
  const tierLabelOf = useTierLabel();
  const { isEventMode: regionEventMode } = useActiveEventMode();
  const isEventMode = eventMode ?? regionEventMode;
  return (
    <div className="relative">
      {/* Column labels */}
      <div className={cn(
        "grid grid-cols-12 px-3 pb-3 text-[10px] font-fun font-black uppercase text-white/45",
        // Compact columns are narrow: long headings (Georgian) wrap inside their own column instead of running into the next.
        compact ? "gap-2 tracking-[0.04em] leading-tight [&>div]:min-w-0 [&>div]:[overflow-wrap:anywhere]" : "gap-2 tracking-[0.18em] sm:gap-4 sm:px-4 sm:text-xs",
      )}>
        <div className={cn(compact ? "col-span-2" : "col-span-3", "text-center")}>{t('leaderboard.colRank')}</div>
        <div className={cn(compact ? "col-span-5" : "col-span-4", "text-left")}>{t('leaderboard.colPlayer')}</div>
        <div className={cn(compact ? "col-span-3" : "col-span-2 sm:col-span-3", "text-center")}>{t('leaderboard.colTier')}</div>
        <div className={cn(compact ? "col-span-2" : "col-span-3 sm:col-span-2", "text-center")}>{pointsLabel ?? t('leaderboard.colRP')}</div>
      </div>

      {/* Table wrapper */}
      <div className="relative">
        {/* Betsson badge — event only, top-right corner of border */}
        {isEventMode && (
          <div
            className="absolute -top-1 -right-2 z-20 flex flex-col items-start rounded-md px-2 py-1"
            style={{ backgroundColor: '#FF6C0A', width: 120, height: 34, rotate: '-5.8deg', border: '2px solid #000', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}
          >
            <span className="text-[6px] font-bold uppercase tracking-wider text-white/80 leading-none">{t('welcome.poweredBy')}</span>
            <Image src="/assets/betsson/3.png" alt="Betsson Sport" width={96} height={18} className="h-4 w-auto object-contain mt-0.5" />
          </div>
        )}

        {/* Bordered table */}
        <div
          className="overflow-visible rounded-[10px] border-2"
          style={{ borderColor: isEventMode ? "#FF6C0A" : "#38B60E" }}
        >
          <div className={cn("divide-y", isEventMode ? "divide-white/5" : "divide-brand-green/25")}>
            {entries.map((entry) => {
              const isCurrentUser = entry.isCurrentUser || entry.id === currentUserId;
              const isFirst = entry.rank === 1;
              const interactive = !!onEntryClick;
              const tierAccent = getTierAccent(entry.tier);

              return (
                <div key={entry.id} className="relative">
                  {/* Row content */}
                  <div
                    onClick={interactive ? () => onEntryClick(entry.id) : undefined}
                    onKeyDown={
                      interactive
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onEntryClick(entry.id);
                            }
                          }
                        : undefined
                    }
                    role={interactive ? "button" : undefined}
                    tabIndex={interactive ? 0 : undefined}
                    className={cn(
                      "grid grid-cols-12 items-center transition-colors",
                      compact ? "gap-2 px-3 py-2.5" : "gap-2 sm:gap-4 px-3 sm:px-4 py-3.5 sm:py-4",
                      isEventMode && isFirst
                        ? "text-white"
                        : isCurrentUser
                          ? "bg-brand-green"
                          : "hover:bg-white/[0.03]",
                      interactive && "cursor-pointer",
                    )}
                    style={isEventMode && isFirst ? { backgroundColor: '#FF6C0A' } : undefined}
                  >
                    {/* Rank */}
                    <div className={cn(compact ? "col-span-2" : "col-span-3", "flex items-center justify-center gap-1.5 sm:gap-2")}>
                      <span
                        className={cn(compact ? "text-base" : "text-xl sm:text-2xl", "tabular-nums font-black text-white")}
                        style={poppins}
                      >
                        #{entry.rank}
                      </span>
                    </div>

                    {/* Player */}
                    <div className={cn(compact ? "col-span-5 gap-2" : "col-span-4 gap-2 sm:gap-3", "flex items-center justify-start min-w-0")}>
                      <div className={compact ? "block" : "block sm:hidden"}>
                        <TierFrameAvatar
                          tier={entry.tier}
                          frameAlt={tierLabelOf(entry.tier)}
                          avatarCustomization={entry.avatarCustomization}
                          avatarFallback={entry.avatar || "avatar-1"}
                          countryCode={entry.country}
                          size="sm"
                        />
                      </div>
                      <div className={compact ? "hidden" : "hidden sm:block"}>
                        <TierFrameAvatar
                          tier={entry.tier}
                          frameAlt={tierLabelOf(entry.tier)}
                          avatarCustomization={entry.avatarCustomization}
                          avatarFallback={entry.avatar || "avatar-1"}
                          countryCode={entry.country}
                          size="md"
                        />
                      </div>
                      <span className={compact
                        ? "min-w-0 whitespace-normal [overflow-wrap:break-word] [word-break:normal] text-xs leading-tight font-fun font-black uppercase text-white"
                        : "truncate text-sm sm:text-base font-fun font-black uppercase text-white"}>
                        {entry.username}
                      </span>
                    </div>

                    {/* Tier */}
                    <div className={cn(compact ? "col-span-3" : "col-span-2 sm:col-span-3", "min-w-0 text-center")}>
                      <span
                        className={compact
                          ? "block whitespace-normal text-[10px] leading-tight font-fun font-black uppercase tracking-wide"
                          : "block truncate text-[10px] sm:text-sm font-fun font-black uppercase tracking-wide"}
                        style={{ color: (isEventMode && isFirst) || isCurrentUser ? "#FFFFFF" : tierAccent }}
                      >
                        {entry.tier || !compact ? tierLabelOf(entry.tier) : t('profileScreen.unranked')}
                      </span>
                    </div>

                    {/* RP */}
                    <div className={cn(compact ? "col-span-2" : "col-span-3 sm:col-span-2", "text-center")}>
                      <span
                        className={cn(compact ? "text-sm" : "text-sm sm:text-lg", "tabular-nums font-black text-white")}
                        style={poppins}
                      >
                        {entry.pointsText ?? entry.rankPoints.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vertical timeline line on the left border — event only */}
        {isEventMode && (
          <div className="absolute -left-[1px] top-0 bottom-0 w-0.5 z-10" style={{ backgroundColor: '#FF6C0A' }} />
        )}
      </div>
    </div>
  );
}
